import { uiText } from '../../i18n/uiText';
import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

export function maskRussianDate(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

export function russianDateToIso(value) {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value || '').trim());
  if (!match) return '';
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (year < MIN_YEAR || year > MAX_YEAR || month < 1 || month > 12 || day < 1 || day > 31) return '';
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function isoDateToRussian(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(String(value || '').trim());
  if (!match) return '';
  const display = `${match[3]}.${match[2]}.${match[1]}`;
  return russianDateToIso(display) ? display : '';
}

export function isSafeIsoDate(value) {
  return Boolean(isoDateToRussian(value));
}

export default function SafeDateInput({ value, onChange, onValidityChange, required = false, id, className = '', ...props }) {
  const textRef = useRef(null);
  const pickerRef = useRef(null);
  const [display, setDisplay] = useState(() => isoDateToRussian(value));
  const [touched, setTouched] = useState(false);

  const parsedValue = russianDateToIso(display);
  const invalid = (required && !display) || (Boolean(display) && !parsedValue);
  const errorText = display
    ? uiText("Введите существующую дату в формате ДД.ММ.ГГГГ (1900–2100).")
    : uiText("Укажите дату.");

  useEffect(() => {
    if (document.activeElement !== textRef.current) {
      setDisplay(isoDateToRussian(value));
    }
  }, [value]);

  useEffect(() => {
    textRef.current?.setCustomValidity(invalid ? errorText : '');
    onValidityChange?.(!invalid);
  }, [errorText, invalid, onValidityChange]);

  const handleTextChange = (event) => {
    const nextDisplay = maskRussianDate(event.target.value);
    const nextIso = russianDateToIso(nextDisplay);
    setDisplay(nextDisplay);
    onChange?.(nextIso);
  };

  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else picker.click();
  };

  return (
    <div className={`safe-date-input${className ? ` ${className}` : ''}`}>
      <div className="safe-date-input-control">
        <input
          {...props}
          ref={textRef}
          id={id}
          type="text"
          className="form-control"
          value={display}
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder={uiText("ДД.ММ.ГГГГ")}
          aria-invalid={touched && invalid ? 'true' : undefined}
          onChange={handleTextChange}
          onBlur={() => setTouched(true)}
        />
        <button type="button" className="safe-date-picker-button" aria-label={uiText("Выбрать дату в календаре")} onClick={openPicker}>
          <CalendarDays size={16} aria-hidden="true" />
        </button>
        <input
          ref={pickerRef}
          className="safe-date-native-picker"
          type="date"
          min="1900-01-01"
          max="2100-12-31"
          value={isSafeIsoDate(value) ? String(value).slice(0, 10) : ''}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const nextIso = event.target.value;
            setDisplay(isoDateToRussian(nextIso));
            setTouched(true);
            onChange?.(nextIso);
          }}
        />
      </div>
      {touched && invalid ? <span className="safe-date-input-error" role="alert">{errorText}</span> : null}
    </div>
  );
}
